<!-- Ticket Main Content Container -->
<div class="ticket-main-content">

  <!-- Ticket Name Input (Create/Edit Mode Only) -->
  @if (mode !== 'view') {
    <div class="ticket-name-section">
      <input type="text"
        class="ticket-name-input"
        [formControl]="ticketNameControl"
        placeholder="Ticket name"
        [attr.aria-label]="'Ticket name'">
    </div>
  }

  <!-- Scrollable Content Area -->
  <div class="ticket-content-scrollable">

    <!-- Description Section -->
    <div class="ticket-description-section">
      <h3 class="section-title">Description</h3>

      <!-- Description Editor (with @mentions support) -->
      <div class="description-editor" [class.readonly]="mode === 'view'">

        <!-- ContentEditable Div (Target for Mentions) -->
        <div
          #descriptionTarget
          id="descriptionTarget"
          class="description-textarea"
          [attr.contenteditable]="mode !== 'view' ? 'true' : 'false'"
          [class.readonly]="mode === 'view'"
          (blur)="onDescriptionChange($event)"
          (input)="onDescriptionInput($event)"
          (keyup)="onDescriptionKeyup($event)"
          [attr.aria-label]="'Ticket description'"
          [attr.data-placeholder]="mode !== 'view' ? 'Add a description... (use @ to mention people)' : ''">
        </div>

      </div>
    </div>

    <!-- Milestone Card (if milestone is selected) -->
    @if (selectedMilestone) {
      <div class="milestone-card" (click)="onMilestoneCardClick()">
        <!-- Milestone Header -->
        <div class="milestone-card-header">
          <div class="milestone-icon-wrapper">
            <mat-icon class="milestone-icon">flag</mat-icon>
          </div>
          <div class="milestone-info">
            <div class="milestone-name">{{ selectedMilestone.name }}</div>
            <div class="milestone-meta">
              <span class="milestone-due-date">
                <mat-icon>calendar_today</mat-icon>
                {{ formatMilestoneDueDate() }}
              </span>
              @if (selectedMilestone.daysRemaining) {
                <span class="milestone-days-remaining">
                  {{ getDaysRemainingText() }}
                </span>
              }
            </div>
          </div>
          <span class="milestone-status-badge" [ngClass]="getMilestoneStatusClass()">
            {{ selectedMilestone.status }}
          </span>
        </div>
        <!-- Milestone Progress (if in-progress) -->
        @if (selectedMilestone.status === 'in-progress' && selectedMilestone.progress !== undefined) {
          <div class="milestone-progress">
            <div class="progress-bar">
              <div class="progress-fill" [style.width]="getMilestoneProgressWidth()"></div>
            </div>
            <span class="progress-text">{{ selectedMilestone.progress }}%</span>
          </div>
        }
      </div>
    }

    <!-- Tabs Section -->
    <div class="ticket-tabs-section">
      <mat-tab-group
        [selectedIndex]="activeTabIndex"
        (selectedIndexChange)="onTabChange($event)"
        class="ticket-tabs"
        animationDuration="200ms">

        <!-- Details Tab -->
        @if (!hideDetailsCommentsHistory) {
          <mat-tab>
            <ng-template mat-tab-label>
              <div class="tab-label">
                <mat-icon>{{ tabs[0].icon }}</mat-icon>
                <span>{{ tabs[0].label }}</span>
                @if (tabs[0].count > 0) {
                  <span class="tab-badge">{{ tabs[0].count }}</span>
                }
              </div>
            </ng-template>
            <div class="tab-content">
              <ng-content select="[details-tab]"></ng-content>
            </div>
          </mat-tab>
        }

        <!-- Checklist Tab -->
        <mat-tab>
          <ng-template mat-tab-label>
            <div class="tab-label">
              <mat-icon>{{ tabs[1].icon }}</mat-icon>
              <span>{{ tabs[1].label }}</span>
              @if (tabs[1].count > 0) {
                <span class="tab-badge">{{ tabs[1].count }}</span>
              }
            </div>
          </ng-template>
          <div class="tab-content">
            <ng-content select="[checklist-tab]"></ng-content>
          </div>
        </mat-tab>

        <!-- Materials & Services Tab — job tickets only -->
        @if (showMaterials) {
        <mat-tab>
          <ng-template mat-tab-label>
            <div class="tab-label">
              <mat-icon>{{ tabs[2].icon }}</mat-icon>
              <span>{{ tabs[2].label }}</span>
              @if (tabs[2].count > 0) {
                <span class="tab-badge">{{ tabs[2].count }}</span>
              }
            </div>
          </ng-template>
          <div class="tab-content">
            <ng-content select="[materials-tab]"></ng-content>
          </div>
        </mat-tab>
        }

        <!-- Attachments Tab -->
        <mat-tab>
          <ng-template mat-tab-label>
            <div class="tab-label">
              <mat-icon>{{ tabs[3].icon }}</mat-icon>
              <span>{{ tabs[3].label }}</span>
              @if (tabs[3].count > 0) {
                <span class="tab-badge">{{ tabs[3].count }}</span>
              }
            </div>
          </ng-template>
          <div class="tab-content">
            <ng-content select="[attachments-tab]"></ng-content>
          </div>
        </mat-tab>

        <!-- Comments Tab -->
        @if (!hideDetailsCommentsHistory) {
          <mat-tab>
            <ng-template mat-tab-label>
              <div class="tab-label">
                <mat-icon>{{ tabs[4].icon }}</mat-icon>
                <span>{{ tabs[4].label }}</span>
                @if (tabs[4].count > 0) {
                  <span class="tab-badge">{{ tabs[4].count }}</span>
                }
              </div>
            </ng-template>
            <div class="tab-content">
              <ng-content select="[comments-tab]"></ng-content>
            </div>
          </mat-tab>
        }

        <!-- History Tab -->
        @if (!hideDetailsCommentsHistory) {
          <mat-tab>
            <ng-template mat-tab-label>
              <div class="tab-label">
                <mat-icon>{{ tabs[5].icon }}</mat-icon>
                <span>{{ tabs[5].label }}</span>
                @if (tabs[5].count > 0) {
                  <span class="tab-badge">{{ tabs[5].count }}</span>
                }
              </div>
            </ng-template>
            <div class="tab-content">
              <ng-content select="[history-tab]"></ng-content>
            </div>
          </mat-tab>
        }

      </mat-tab-group>
    </div>

  </div>

</div>

<!-- Custom Mention Dropdown (Portal to body with fixed positioning) -->
@if (showMentionDropdown) {
  <div class="custom-mention-dropdown-portal">
    <div class="custom-mention-dropdown"
      [style.top.px]="mentionDropdownTop"
      [style.left.px]="mentionDropdownLeft">
      <!-- Loading State -->
      @if (isMentionsLoading) {
        <div class="mention-loading">
          <mat-spinner diameter="20"></mat-spinner>
          <span>Searching...</span>
        </div>
      }
      <!-- User List -->
      @if (!isMentionsLoading && mentions.length > 0) {
        <div class="mention-list">
          @for (user of mentions; track user; let i = $index) {
            <div class="mention-item"
              [class.active]="i === selectedMentionIndex"
              (click)="selectMention(user)"
              (mouseenter)="selectedMentionIndex = i">
              <app-profile-avatar
                [fullName]="user.full_name"
                [profileUrl]=" $any(user.profile_image)?.url || ''"
                widthSize="32px"
                heightSize="32px">
              </app-profile-avatar>
              <div class="mention-user-info">
                <div class="mention-user-name">{{user.full_name}}</div>
                <!-- <div class="mention-user-email" *ngIf="user.email">{{user.email}}</div> -->
              </div>
            </div>
          }
        </div>
      }
      <!-- No Results -->
      @if (!isMentionsLoading && mentions.length === 0) {
        <div class="mention-no-results">
          <mat-icon>search_off</mat-icon>
          <span>No users found</span>
        </div>
      }
    </div>
  </div>
}
